import React, { useEffect, useMemo, useState } from 'react';
import api from '../../../../utils/api';
import './CriticidadeFaturamentoReport.css';

interface Venda {
  id?: number;
  data_venda?: string;
  valor_total?: number;
  cliente?: string;
  produto?: string;
  quantidade?: number;
}

interface FiltroPeriodo {
  dataInicio: string;
  dataFim: string;
}

interface CriticidadeItem {
  nome: string;
  totalFaturamento: number;
  quantidadeVendas: number;
  ticketMedio: number;
  status: 'crítico' | 'alerta' | 'normal';
  percentualMeta: number;
}

const toDateParam = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getPeriodoInicial = (): FiltroPeriodo => {
  const hoje = new Date();
  const inicio = new Date(hoje.getTime() - 1000 * 60 * 60 * 24 * 90);
  return {
    dataInicio: toDateParam(inicio),
    dataFim: toDateParam(hoje),
  };
};

const parseValor = (value: unknown) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const formatNumber = (value: number) => new Intl.NumberFormat('pt-BR').format(value);

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 2,
  }).format(value);
};

const getStatusColor = (status: 'crítico' | 'alerta' | 'normal') => {
  switch (status) {
    case 'crítico': return '#EF4444';
    case 'alerta': return '#F59E0B';
    case 'normal': return '#22C55E';
  }
};

const getCriticidadeStatus = (
  totalFaturamento: number,
  metaMinima: number
): 'crítico' | 'alerta' | 'normal' => {
  if (totalFaturamento < metaMinima * 0.5) return 'crítico';
  if (totalFaturamento < metaMinima * 0.75) return 'alerta';
  return 'normal';
};

const CriticidadeFaturamentoReport: React.FC = () => {
  const [periodo, setPeriodo] = useState<FiltroPeriodo>(() => getPeriodoInicial());
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [vendas, setVendas] = useState<Venda[]>([]);
  const [filtroStatus, setFiltroStatus] = useState<'todos' | 'crítico' | 'alerta' | 'normal'>('todos');

  const metaMensalMinima = 50000;

  const carregar = async () => {
    setLoading(true);
    setErro('');

    try {
      const response = await api.get('/vendas', {
        params: {
          dataInicio: periodo.dataInicio,
          dataFim: periodo.dataFim,
        },
      });

      setVendas(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Não foi possível carregar o faturamento no momento.';
      setErro(message);
      setVendas([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const analisisCriticidade = useMemo(() => {
    const mapaClientes = new Map<string, { faturamento: number; vendas: number }>();

    vendas.forEach(item => {
      const cliente = String(item.cliente || 'Não informado').trim() || 'Não informado';
      const atual = mapaClientes.get(cliente) || { faturamento: 0, vendas: 0 };
      atual.faturamento += parseValor(item.valor_total);
      atual.vendas += 1;
      mapaClientes.set(cliente, atual);
    });

    const items: CriticidadeItem[] = Array.from(mapaClientes.entries()).map(([nome, values]) => {
      const totalFaturamento = values.faturamento;
      const quantidadeVendas = values.vendas;
      const ticketMedio = quantidadeVendas ? totalFaturamento / quantidadeVendas : 0;
      const status = getCriticidadeStatus(totalFaturamento, metaMensalMinima);
      const percentualMeta = (totalFaturamento / metaMensalMinima) * 100;

      return {
        nome,
        totalFaturamento,
        quantidadeVendas,
        ticketMedio,
        status,
        percentualMeta,
      };
    });

    return items.sort((a, b) => {
      const statusOrder = { crítico: 0, alerta: 1, normal: 2 };
      if (statusOrder[a.status] !== statusOrder[b.status]) {
        return statusOrder[a.status] - statusOrder[b.status];
      }
      return b.totalFaturamento - a.totalFaturamento;
    });
  }, [vendas]);

  const itemsFiltrados = useMemo(() => {
    if (filtroStatus === 'todos') return analisisCriticidade;
    return analisisCriticidade.filter(item => item.status === filtroStatus);
  }, [analisisCriticidade, filtroStatus]);

  const resumoStatus = useMemo(() => {
    return {
      critico: analisisCriticidade.filter(item => item.status === 'crítico').length,
      alerta: analisisCriticidade.filter(item => item.status === 'alerta').length,
      normal: analisisCriticidade.filter(item => item.status === 'normal').length,
      total: analisisCriticidade.length,
    };
  }, [analisisCriticidade]);

  const totalFaturamento = useMemo(() => {
    return vendas.reduce((acc, item) => acc + parseValor(item.valor_total), 0);
  }, [vendas]);

  const exportCsv = () => {
    const headers = ['Cliente', 'Faturamento', 'Vendas', 'Ticket Médio', '% Meta', 'Status'];
    const escape = (value: unknown) => {
      const text = String(value ?? '').replace(/"/g, '""').replace(/\r?\n/g, ' ');
      return `"${text}"`;
    };

    const content = [
      '\ufeff',
      headers.map(escape).join(';'),
      ...itemsFiltrados.map((item) => [
        item.nome,
        item.totalFaturamento.toFixed(2),
        item.quantidadeVendas,
        item.ticketMedio.toFixed(2),
        item.percentualMeta.toFixed(1),
        item.status,
      ].map(escape).join(';')),
    ].join('\n');

    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `criticidade_faturamento_${Date.now()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="crit-report crit-pbi-canvas">
      <div className="crit-pbi-header">
        <div>
          <h3>Criticidade do Faturamento de Veículos</h3>
          <p>Análise de impacto financeiro por responsável, com identificação de riscos e oportunidades.</p>
        </div>
      </div>

      <div className="crit-toolbar crit-visual-card">
        <div className="crit-filtros">
          <label>
            Início
            <input
              type="date"
              value={periodo.dataInicio}
              onChange={(e) => setPeriodo(prev => ({ ...prev, dataInicio: e.target.value }))}
            />
          </label>
          <label>
            Fim
            <input
              type="date"
              value={periodo.dataFim}
              onChange={(e) => setPeriodo(prev => ({ ...prev, dataFim: e.target.value }))}
            />
          </label>
          <label>
            Status
            <select value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value as any)}>
              <option value="todos">Todos</option>
              <option value="crítico">🔴 Crítico</option>
              <option value="alerta">🟡 Alerta</option>
              <option value="normal">🟢 Normal</option>
            </select>
          </label>
        </div>
        <div className="crit-actions">
          <button className="crit-btn" onClick={carregar} disabled={loading}>
            {loading ? 'Atualizando...' : 'Atualizar'}
          </button>
          <button className="crit-btn crit-btn-ghost" onClick={exportCsv} disabled={!itemsFiltrados.length}>
            Exportar CSV
          </button>
        </div>
      </div>

      {erro && <div className="crit-error">{erro}</div>}

      <div className="crit-kpis">
        <div className="crit-kpi-card crit-visual-card">
          <span>Faturamento Total</span>
          <strong>{formatCurrency(totalFaturamento)}</strong>
        </div>
        <div className="crit-kpi-card crit-visual-card crit-status-critico">
          <span>🔴 Crítico</span>
          <strong>{resumoStatus.critico}</strong>
        </div>
        <div className="crit-kpi-card crit-visual-card crit-status-alerta">
          <span>🟡 Alerta</span>
          <strong>{resumoStatus.alerta}</strong>
        </div>
        <div className="crit-kpi-card crit-visual-card crit-status-normal">
          <span>🟢 Normal</span>
          <strong>{resumoStatus.normal}</strong>
        </div>
      </div>

      <div className="crit-table-wrap crit-visual-card">
        <h4>Análise por Responsável</h4>
        <div className="crit-table-scroll">
          <table className="crit-table">
            <thead>
              <tr>
                <th>Cliente/Responsável</th>
                <th>Faturamento</th>
                <th>Vendas</th>
                <th>Ticket Médio</th>
                <th>% Meta</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {itemsFiltrados.length === 0 ? (
                <tr>
                  <td colSpan={6} className="crit-empty-cell">Nenhum registro encontrado.</td>
                </tr>
              ) : (
                itemsFiltrados.map((item, index) => (
                  <tr key={`${item.nome}-${index}`}>
                    <td className="crit-nome">{item.nome}</td>
                    <td>{formatCurrency(item.totalFaturamento)}</td>
                    <td>{formatNumber(item.quantidadeVendas)}</td>
                    <td>{formatCurrency(item.ticketMedio)}</td>
                    <td>
                      <div className="crit-progress-bar">
                        <div
                          className="crit-progress-fill"
                          style={{
                            width: `${Math.min(item.percentualMeta, 100)}%`,
                            backgroundColor: getStatusColor(item.status),
                          }}
                        />
                        <span className="crit-progress-text">{item.percentualMeta.toFixed(0)}%</span>
                      </div>
                    </td>
                    <td>
                      <span
                        className="crit-status-badge"
                        style={{ backgroundColor: getStatusColor(item.status) }}
                      >
                        {item.status === 'crítico' ? '🔴' : item.status === 'alerta' ? '🟡' : '🟢'} {item.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CriticidadeFaturamentoReport;
